import React, { useEffect, useCallback, useRef } from 'react';
import Header from "@Layout/Header";
import Footer from "@Layout/Footer";
import { updateResized, loadFonts, useStore, setSmoothScroll, setHeaderBG } from "@UseCase/store";
import { getClampValue } from '@UseCase/style';
import FeatureJungle from '@Src/fonts/FeatureJungle-Regular.woff2';
import FeatureJungleLight from '@Src/fonts/FeatureJungle-Light.woff2';
import UniversalJungleBold from '@Src/fonts/UniversalJungle-515.woff2';
import UniversalJungle from '@Src/fonts/UniversalJungle-420.woff2';
import { Helmet } from 'react-helmet';
import FontFaceObserver from 'fontfaceobserver';
import debounce from 'lodash/debounce';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { isSafari, isEdge, isIOS } from 'react-device-detect';
import PropTypes from 'prop-types';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const LayoutWrapper = ({ children, location }) => {
  const [store, dispatch] = useStore();
  const $header = useRef();
  const smoothScrollRef = useRef();

  // Helpers
  const setViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  const setHeaderHeight = () => {
    if ($header.current) {
      document.documentElement.style.setProperty(
        '--headerHeight',
        `${$header.current.offsetHeight}px`
      );
    }
  };

  const safeRefresh = useCallback(() => {
    const smoother = ScrollSmoother.get();
    if (smoother && document.querySelector('#smooth-content')) {
      smoother.refresh();
    }
  }, []);

  const safeScrollTop = useCallback((y = 1) => {
    const smoother = ScrollSmoother.get();
    if (smoother && document.querySelector('#smooth-content')) {
      smoother.scrollTo(y, false); // instant scroll
    }
  }, []);

  const initSmoothScroll = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!ScrollSmoother.get()) {
      const smoother = ScrollSmoother.create({
        smooth: 1.3,
        wrapper: '#gatsby-focus-wrapper',
        content: '#smooth-content',
        normalizeScroll: isIOS,
        effects: true,
      });
      setSmoothScroll(dispatch, smoother);
    }
  }, [dispatch]);

  // Init smoother once
  useEffect(() => {
    initSmoothScroll();
  }, [initSmoothScroll]);

  // Keep ref updated
  useEffect(() => {
    smoothScrollRef.current = store.smoothScroll;
  }, [store.smoothScroll]);

  // Route change handling (without scrollFix)
  useEffect(() => {
    const onRouteChange = () => {
      setHeaderBG(dispatch, false);

      // Wait for new DOM to mount before refreshing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          safeRefresh();
          safeScrollTop(1);
        });
      });
    };

    window.addEventListener('gatsby:routeUpdated', onRouteChange);
    window.addEventListener('popstate', onRouteChange);

    const onResize = () => {
      setViewportHeight();
      setHeaderHeight();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('gatsby:routeUpdated', onRouteChange);
      window.removeEventListener('resize', onResize);
    };
  }, [dispatch, safeRefresh, safeScrollTop]);

  // Fonts load
  const handleWebfontLoad = useCallback(() => {
    const observers = [];
    const fontData = {
      'Feature Jungle': { weight: 700 },
      'Universal Jungle': { weight: 400 },
    };
    Object.keys(fontData).forEach(family => {
      const data = fontData[family];
      observers.push(new FontFaceObserver(family, data).load());
    });

    Promise.all(observers)
      .then(() => loadFonts(dispatch))
      .catch(err => console.warn('Some critical fonts are not available', err));
  }, [dispatch]);

  // Resize observer
  useEffect(() => {
    setViewportHeight();
    setHeaderHeight();
    handleWebfontLoad();

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        updateResized(dispatch);
        setViewportHeight();
        setHeaderHeight();
      }, 50)
    );
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [handleWebfontLoad, dispatch]);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>{`
          :root {
            --menu-font-size: ${getClampValue(24, 80)};
            --5xl-font-size: ${getClampValue(72, 120)};
            --4xl-font-size: ${getClampValue(40, 72)};
            --35xl-font-size: ${getClampValue(32, 65)};
            --3xl-font-size: ${getClampValue(40, 56)};
            --2xl-font-size: ${getClampValue(24, 32)};
            --xl-font-size: ${getClampValue(32, 40)};
            --lg-font-size: ${getClampValue(18, 24)};
          }
          @font-face {
            font-family: 'Universal Jungle';
            src: url(${UniversalJungleBold}) format('woff2');
            font-weight: 700;
            font-display: swap;
          }
          @font-face {
            font-family: 'Universal Jungle';
            src: url(${UniversalJungle}) format('woff2');
            font-weight: 300;
            font-display: swap;
          }
          @font-face {
            font-family: 'Feature Jungle';
            src: url(${FeatureJungle}) format('woff2');
            font-weight: 700;
            font-display: swap;
          }
          @font-face {
            font-family: 'Feature Jungle';
            src: url(${FeatureJungleLight}) format('woff2');
            font-weight: 400;
            font-display: swap;
          }
          section {
            will-change: transform;
            margin-top: -1px;
          }
        `}</style>
        <script src="https://js-eu1.hsforms.net/forms/embed/145756322.js" defer></script>
      </Helmet>
      <Header ref={$header} location={location} />
      <div
        id="smooth-content"
        className={`${(isSafari || isEdge) ? 'boxedDevices' : ''} content flex flex-col`}
      >
        <main key={location?.pathname}>{children}</main>
        <Footer location={location} />
      </div>
    </>
  );
};

LayoutWrapper.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object,
};

export default LayoutWrapper;
